import React, { useState } from "react";
import apiClient from "../api/apiClient";
import "../pages/AuthPage.css";

const SurveyGameDialog = ({ quiz, onClose }) => {
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const allAnswered = quiz.questions.every(
    (question) => answers[question.id] !== undefined,
  );

  const chooseAnswer = (question, answerIndex) => {
    if (result) return;

    setAnswers((current) => ({ ...current, [question.id]: answerIndex }));
    setFeedback((current) => ({ ...current, [question.id]: "selected" }));
  };

  const submitQuiz = async () => {
    setError("");
    setSubmitting(true);

    try {
      const { data } = await apiClient.post("/survey-game/submit", {
        answers: quiz.questions.map((question) => ({
          question_id: question.id,
          answer_index: answers[question.id],
        })),
      });

      setResult(data);
      setFeedback(
        Object.fromEntries(
          data.results.map((item) => [
            item.question_id,
            item.is_correct ? "right" : "wrong",
          ]),
        ),
      );
    } catch (err) {
      setError(
        err.response?.data?.message
          || "Could not submit your answers. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="survey-game-backdrop" role="dialog" aria-modal="true" aria-labelledby="survey-game-title">
      <div className="survey-game-shell">
        <button
          type="button"
          className="survey-game-close"
          onClick={onClose}
          aria-label="Close survey challenge"
        >
          &times;
        </button>
        <div className={`survey-game-dialog ${result?.won ? "survey-game-won" : ""}`}>
        <div className="survey-game-header">
          <span>First Month Challenge</span>
          <h3 id="survey-game-title">Answer every question correctly to win a free subscription.</h3>
          <p>
            {result
              ? `You scored ${result.score}/${result.total}.`
              : `${quiz.question_count} gym questions. One shot. Trust your training brain.`}
          </p>
        </div>

        <div className="survey-game-questions">
          {quiz.questions.map((question, questionIndex) => {
            const resultItem = result?.results?.find(
              (item) => item.question_id === question.id,
            );

            return (
              <div
                className={`survey-game-question survey-game-${feedback[question.id] || "idle"}`}
                key={question.id}
              >
                <div className="survey-game-question-title">
                  <span>{questionIndex + 1}</span>
                  <strong>{question.question}</strong>
                </div>
                <div className="survey-game-options">
                  {question.options.map((option, optionIndex) => {
                    const isSelected = answers[question.id] === optionIndex;
                    const isCorrect = resultItem?.correct_index === optionIndex;
                    const isWrongSelection = resultItem && isSelected && !isCorrect;

                    return (
                      <button
                        type="button"
                        key={`${question.id}-${optionIndex}`}
                        className={[
                          "survey-game-option",
                          isSelected ? "selected" : "",
                          resultItem && isCorrect ? "correct" : "",
                          isWrongSelection ? "wrong" : "",
                        ].join(" ")}
                        onClick={() => chooseAnswer(question, optionIndex)}
                        disabled={!!result}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                {resultItem && (
                  <div className="survey-game-answer">
                    Correct answer: <strong>{resultItem.correct_answer}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {result ? (
          <div className="survey-game-result">
            <h4>
              {result.won
                ? "You won your free first month!"
                : "Nice try. Your answers are shown above."}
            </h4>
            <p>
              {result.won
                ? "Your one-month subscription has been activated on your account."
                : "The game is complete, and the correct answers are now visible for review."}
            </p>
            <button type="button" className="btn btn-primary btn-skew" onClick={onClose}>
              <span>Continue</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`btn btn-primary btn-skew w-100 survey-game-submit ${submitting ? "auth-submit-loading" : ""}`}
            onClick={submitQuiz}
            disabled={!allAnswered || submitting}
          >
            <span>{submitting ? "Checking answers" : "Submit Answers"}</span>
          </button>
        )}
        </div>
      </div>
    </div>
  );
};

export default SurveyGameDialog;
